import React from 'react';
import { ActionButton, Tooltip, TooltipTrigger } from '@adobe/react-spectrum';

interface ToolTipWrapperProps {
  children: React.ReactNode;
  message?: string | React.ReactNode;
  constrainWidth?: boolean; // New prop to control width constraints for ellipsis
  inlineFlex?: boolean; // When true, wrapper behaves as inline-flex for alignment
  delay?: number; // Custom delay in milliseconds (default: 300ms)
  isOpen?: boolean; // Controlled open state (for manual timer management)
  onOpenChange?: (isOpen: boolean) => void; // Callback for open state changes
  onMouseEnter?: () => void; // Custom mouse enter handler
  onMouseLeave?: () => void; // Custom mouse leave handler
  placement?: 'top' | 'bottom' | 'left' | 'right';
  cursor?: 'help' | 'pointer' | 'default';
  ariaLabel?: string;
  onPress?: () => void;
}

const ToolTipWrapper = ({
  children,
  message,
  constrainWidth = false,
  inlineFlex = false,
  delay = 300,
  isOpen,
  onOpenChange,
  onMouseEnter,
  onMouseLeave,
  placement,
  cursor = 'help',
  ariaLabel = 'Edit Name',
  onPress
}: ToolTipWrapperProps) => {
  if (
    !message ||
    (typeof message === 'string' && message.trim().length === 0)
  ) {
    return <>{children}</>;
  }

  // If manual control is provided (isOpen is defined), use controlled mode without built-in delay
  const isControlled = isOpen !== undefined;

  const content = (
    <ActionButton
      isQuiet
      UNSAFE_style={{
        all: 'unset', // behave like text
        cursor, // indicate help/tooltip or pointer for icon actions
        color: 'inherit', // inherit text color
        font: 'inherit', // inherit font family, size, weight
        lineHeight: 'inherit',
        textDecoration: 'inherit', // keep surrounding text decoration
        padding: 0,
        margin: 0,
        background: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        outline: 'none',
        // Apply constrained styles only for dropdown fields with ellipsis
        width: constrainWidth ? '100%' : 'fit-content',
        maxWidth: constrainWidth ? '100%' : undefined,
        minWidth: inlineFlex || constrainWidth ? 0 : undefined,
        display: inlineFlex ? 'inline-flex' : constrainWidth ? 'block' : 'ruby',
        alignItems: inlineFlex ? 'center' : undefined,
        verticalAlign: inlineFlex ? 'middle' : undefined,
        overflow: constrainWidth ? 'hidden' : undefined
      }}
      aria-label={ariaLabel}
      onPress={onPress}
    >
      {children}
    </ActionButton>
  );

  return (
    <TooltipTrigger
      delay={isControlled ? 0 : delay}
      isOpen={isControlled ? isOpen : undefined}
      onOpenChange={isControlled ? onOpenChange : undefined}
      placement={placement}
    >
      {isControlled && (onMouseEnter || onMouseLeave) ? (
        <div
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          style={{ display: inlineFlex ? 'inline-flex' : 'contents' }}
        >
          {content}
        </div>
      ) : (
        content
      )}
      <Tooltip>{message}</Tooltip>
    </TooltipTrigger>
  );
};

export default ToolTipWrapper;
